import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button, FormControl } from 'react-bootstrap';
import Utils from '../util/Utils.jsx';
import { openAlert } from '../context/alert.js';
import { formatVersionIdentity } from '../util/scheduleVersionIdentity.js';

const activityLabel = row => {
	if (!row) {
		return '';
	}
	const code = row.taskCode || '';
	const name = row.name || '(unnamed)';
	return code ? `${code} — ${name}` : name;
};

const keyOf = row => (row ? `${row.keyType}:${row.key}` : '');

const ScheduleMatchReview = ({
	datasetId,
	reviewVersion,
	preview,
	review,
	busy,
	dispatch,
	onChanged
}) => {
	const [selectedCurrent, setSelectedCurrent] = useState(null);
	const [selectedOther, setSelectedOther] = useState(null);
	const [suggestions, setSuggestions] = useState([]);
	const [suggesting, setSuggesting] = useState(false);
	const [suggestMeta, setSuggestMeta] = useState(null);
	const [pairOtherKey, setPairOtherKey] = useState('');

	if (!reviewVersion || !preview || reviewVersion.isDisplayed) {
		return null;
	}

	const unmatchedCurrent = (review && review.unmatchedCurrent) || [];
	const unmatchedOther = (review && review.unmatchedOther) || [];
	const working = busy || suggesting;

	const resetSelection = () => {
		setSelectedCurrent(null);
		setSelectedOther(null);
		setPairOtherKey('');
	};

	const saveLink = (payload) => {
		if (working) {
			return;
		}
		Utils.ajaxAsync('setScheduleMatchLink', {
			dataset: String(datasetId),
			versionId: String(reviewVersion.id),
			...payload
		}, dispatch, { suppressErrorAlert: true })
			.then(result => {
				if (!result || !result.success) {
					dispatch(openAlert('Match link failed', (result && result.error) || 'Unable to save that link.'));
					return;
				}
				resetSelection();
				if (typeof onChanged === 'function') {
					onChanged(result);
				}
			})
			.catch(() => {
				dispatch(openAlert('Match link failed', 'Unable to save that link.'));
			});
	};

	const pairRows = (fromRow, toRow) => {
		if (!fromRow || !toRow) {
			return;
		}
		saveLink({
			action: 'save',
			linkType: 'user',
			fromKeyType: fromRow.keyType,
			fromKey: fromRow.key,
			toKeyType: toRow.keyType,
			toKey: toRow.key
		});
	};

	const markNewScope = row => {
		saveLink({
			action: 'save',
			linkType: 'new_scope',
			fromKeyType: row.keyType,
			fromKey: row.key
		});
	};

	const markDropped = row => {
		saveLink({
			action: 'save',
			linkType: 'dropped',
			toKeyType: row.keyType,
			toKey: row.key
		});
	};

	const clickCurrent = row => {
		if (selectedOther) {
			pairRows(row, selectedOther);
			return;
		}
		setSelectedCurrent(keyOf(row) === keyOf(selectedCurrent) ? null : row);
	};

	const clickOther = row => {
		if (selectedCurrent) {
			pairRows(selectedCurrent, row);
			return;
		}
		setSelectedOther(keyOf(row) === keyOf(selectedOther) ? null : row);
	};

	const suggestMatches = () => {
		if (working) {
			return;
		}
		setSuggesting(true);
		Utils.ajaxAsync('suggestScheduleMatches', {
			dataset: String(datasetId),
			versionId: String(reviewVersion.id)
		}, dispatch, { suppressErrorAlert: true })
			.then(result => {
				if (!result || !result.success) {
					dispatch(openAlert(
						'Suggest matches failed',
						(result && result.error) || 'Local AI and Gemini fallback were unavailable.'
					));
					return;
				}
				setSuggestions(Array.isArray(result.suggestions) ? result.suggestions : []);
				setSuggestMeta({
					provider: result.provider,
					model: result.model,
					truncated: !!result.truncated
				});
				if (typeof onChanged === 'function' && (result.matchPreview || result.matchReview)) {
					onChanged(result);
				}
			})
			.catch(() => {
				dispatch(openAlert('Suggest matches failed', 'Unable to reach the match suggestion service.'));
			})
			.finally(() => {
				setSuggesting(false);
			});
	};

	const acceptSuggestion = suggestion => {
		pairRows(suggestion.from, suggestion.to);
		setSuggestions(prev => prev.filter(item => keyOf(item.from) !== keyOf(suggestion.from)));
	};

	const dismissSuggestion = suggestion => {
		setSuggestions(prev => prev.filter(item => keyOf(item.from) !== keyOf(suggestion.from)));
	};

	const providerNote = suggestMeta && suggestMeta.provider === 'gemini'
		? 'Suggested via Gemini (LAN model unavailable). Accept still required.'
		: (suggestMeta && suggestMeta.provider === 'local'
			? 'Suggested by the local LAN model. Accept still required.'
			: '');

	return (
		<div className="scheduleMatchReview">
			<div className="scheduleMatchReviewHeader">
				<div>
					<h5>Match review</h5>
					<p>
						Unmatched in the displayed schedule vs {formatVersionIdentity(reviewVersion, { alwaysIncludeId: true })}.
						Click one activity in each list to save an override, or use the dropdown.
						New scope and dropped stop looking like errors. AI only suggests — it never writes a link.
					</p>
				</div>
				<Button
					bsStyle="default"
					disabled={working || !unmatchedCurrent.length || !unmatchedOther.length}
					onClick={suggestMatches}>
					{suggesting ? 'Suggesting…' : 'Suggest matches (local AI)'}
				</Button>
			</div>
			{providerNote ? <p className="scheduleMatchReviewHint">{providerNote}</p> : null}
			{suggestMeta && suggestMeta.truncated
				? <p className="scheduleMatchReviewHint">Prompt capped to keep the model context small. Pair the rest by hand or run Suggest again after accepting.</p>
				: null}
			{suggestions.length
				? (
					<div className="scheduleMatchSuggestions">
						{suggestions.map(suggestion => (
							<div className="scheduleMatchSuggestion" key={`${keyOf(suggestion.from)}-${keyOf(suggestion.to)}`}>
								<div>
									<strong>{activityLabel(suggestion.from)}</strong>
									{' → '}
									<strong>{activityLabel(suggestion.to)}</strong>
									{suggestion.reason ? <span> — {suggestion.reason}</span> : null}
								</div>
								<div className="scheduleMatchSuggestionActions">
									<Button
										bsSize="small"
										bsStyle="primary"
										disabled={working}
										onClick={() => acceptSuggestion(suggestion)}>
										Accept
									</Button>
									<Button
										bsSize="small"
										disabled={working}
										onClick={() => dismissSuggestion(suggestion)}>
										Dismiss
									</Button>
								</div>
							</div>
						))}
					</div>
				)
				: null}
			<div className="scheduleMatchColumns">
				<div className="scheduleMatchColumn">
					<h6>Unmatched in current ({unmatchedCurrent.length})</h6>
					{unmatchedCurrent.length
						? unmatchedCurrent.map(row => (
							<div
								key={keyOf(row)}
								className={keyOf(row) === keyOf(selectedCurrent) ? 'scheduleMatchItem isSelected' : 'scheduleMatchItem'}
								onClick={() => clickCurrent(row)}
								onKeyDown={event => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault();
										clickCurrent(row);
									}
								}}
								role="button"
								tabIndex={0}>
								<div className="scheduleMatchItemTitle">{activityLabel(row)}</div>
								{row.wbs ? <div className="scheduleMatchItemMeta">{row.wbs}</div> : null}
								<div className="scheduleMatchItemActions">
									<Button
										bsSize="small"
										disabled={working}
										onClick={event => {
											event.stopPropagation();
											markNewScope(row);
										}}>
										New scope
									</Button>
								</div>
							</div>
						))
						: <div className="scheduleMatchEmpty">All current activities are matched or marked new scope.</div>}
				</div>
				<div className="scheduleMatchColumn">
					<h6>Unmatched in other file ({unmatchedOther.length})</h6>
					{unmatchedOther.length
						? unmatchedOther.map(row => (
							<div
								key={keyOf(row)}
								className={keyOf(row) === keyOf(selectedOther) ? 'scheduleMatchItem isSelected' : 'scheduleMatchItem'}
								onClick={() => clickOther(row)}
								onKeyDown={event => {
									if (event.key === 'Enter' || event.key === ' ') {
										event.preventDefault();
										clickOther(row);
									}
								}}
								role="button"
								tabIndex={0}>
								<div className="scheduleMatchItemTitle">{activityLabel(row)}</div>
								{row.wbs ? <div className="scheduleMatchItemMeta">{row.wbs}</div> : null}
								<div className="scheduleMatchItemActions">
									<Button
										bsSize="small"
										disabled={working}
										onClick={event => {
											event.stopPropagation();
											markDropped(row);
										}}>
										Dropped
									</Button>
								</div>
							</div>
						))
						: <div className="scheduleMatchEmpty">All other-file activities are matched or marked dropped.</div>}
				</div>
			</div>
			{unmatchedCurrent.length && unmatchedOther.length
				? (
					<div className="scheduleMatchDropdown">
						<label htmlFor={`schedule-match-other-${datasetId}`}>Or pair the selected current activity</label>
						<FormControl
							id={`schedule-match-other-${datasetId}`}
							componentClass="select"
							className="setupInput"
							disabled={working || !selectedCurrent}
							value={pairOtherKey}
							onChange={event => {
								const value = event.target.value;
								setPairOtherKey(value);
								const other = unmatchedOther.find(row => keyOf(row) === value);
								if (selectedCurrent && other) {
									pairRows(selectedCurrent, other);
								}
							}}>
							<option value="">{selectedCurrent ? 'Choose an unmatched activity…' : 'Select a current activity first'}</option>
							{unmatchedOther.map(row => (
								<option key={keyOf(row)} value={keyOf(row)}>{activityLabel(row)}</option>
							))}
						</FormControl>
					</div>
				)
				: null}
		</div>
	);
};

export default ScheduleMatchReview;

ScheduleMatchReview.propTypes = {
	datasetId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
	reviewVersion: PropTypes.object,
	preview: PropTypes.object,
	review: PropTypes.object,
	busy: PropTypes.bool,
	dispatch: PropTypes.func.isRequired,
	onChanged: PropTypes.func
};
